import {
  useFilteredUsers,
  useOrganizations,
} from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { getCurrentFiscalYear } from '@/utils';
import { useMemo, useState } from 'react';
import type { z } from 'zod';
import type {
  ProjectFormSchema,
  ProjectModel,
} from '../projects/_models/project';
import { convertFormSchemaToRequestProjectMode } from '../review-sheet/utils/format-project-data';
import { useAllConsecutiveIndicators, useIndicators } from './activity';
import { useProject } from './project';

type FormValues = z.infer<typeof ProjectFormSchema>;

type getUserAndOrganizationIdsFromProjectArgs = (
  | { data: FormValues; type: 'form' }
  | {
      data: z.infer<typeof ProjectModel> | undefined;
      type: 'initial';
    }
) & { projectId: string };

const getUserAndOrganizationIdsFromProject = ({
  data,
  type,
  projectId,
}: getUserAndOrganizationIdsFromProjectArgs) => {
  const values =
    type === 'form'
      ? convertFormSchemaToRequestProjectMode({
          values: { id: projectId, ...data },
          type: 'update',
        })
      : data;

  const additionalJurisdictionalUsers =
    values?.sheetType === 'KS' &&
    values.fundAdditionalAttributes?.fundSheetType === 'standard'
      ? values.fundAdditionalAttributes.additionalJurisdictionalUsers
      : undefined;

  const additionalJurisdictionalBusinessManagerIds =
    additionalJurisdictionalUsers?.flatMap((v) => v.businessManagerIds) ?? [];

  const additionalJurisdictionalPeopleInChargeIds =
    additionalJurisdictionalUsers?.flatMap((v) => v.peopleInChargeIds) ?? [];

  const additionalJurisdictionalOrganizationId =
    additionalJurisdictionalUsers?.flatMap((v) => v.organizationId ?? []) ?? [];

  const organizationIds = values?.organizationId ? [values.organizationId] : [];
  const relatedOrganizationIds = values?.relatedOrganizationIds ?? [];

  const otherResponsibleOrganizationIds =
    values?.otherResponsibleOrganizationIds ?? [];

  const peopleInChargeIds = values?.peopleInChargeIds ?? [];
  const businessManagerIds = values?.businessManagerIds ?? [];

  return {
    userIds: [
      ...peopleInChargeIds,
      ...businessManagerIds,
      ...additionalJurisdictionalPeopleInChargeIds,
      ...additionalJurisdictionalBusinessManagerIds,
    ],
    organizationIds: [
      ...additionalJurisdictionalOrganizationId,
      ...organizationIds,
      ...relatedOrganizationIds,
      ...otherResponsibleOrganizationIds,
    ],
  };
};

const getCountMapById = (arr: string[]): Record<string, number> => {
  return arr.reduce<Record<string, number>>((acc, id) => {
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});
};

export const useCheckRemoveNotExistUserIdOrNotExistOrganizationId = () => {
  const { id } = useId();
  const { data: projectData } = useProject(id);
  const [openUpdateConfirmModal, setOpenUpdateConfirmModal] = useState<
    | {
        isOpen: true;
        values: FormValues;
      }
    | { isOpen: false }
  >({ isOpen: false });

  const initialUserAndOrganization = getUserAndOrganizationIdsFromProject({
    type: 'initial',
    data: projectData,
    projectId: id,
  });
  const { data: notExistUserIds } = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: initialUserAndOrganization.userIds,
    },
    enabled: !!initialUserAndOrganization.userIds.length,
    select: (data) => {
      const ids = data.results.map((d) => d.id);
      return initialUserAndOrganization.userIds.flatMap((v) =>
        ids.includes(v) ? [] : [v],
      );
    },
  });

  const { data: notExistOrganizationIds } = useOrganizations({
    select: (data) => {
      const ids = data.map((d) => d.id);
      return initialUserAndOrganization.organizationIds.flatMap((v) =>
        ids.includes(v) ? [] : [v],
      );
    },
  });

  // Note that the display conditions and error wording are different.
  // FLY: https://weldrow.slack.com/archives/C05454EH8PK/p1716351049215549?thread_ts=1715068196.468919&cid=C05454EH8PK
  const checkRemoveNotExistUserIdOrNotExistOrganizationId = async (
    values: FormValues,
  ): Promise<{ isRemovedNotExistValue: boolean }> => {
    if (!notExistUserIds?.length && !notExistOrganizationIds?.length) {
      return { isRemovedNotExistValue: false };
    }
    const { userIds: inputUserIds, organizationIds: inputOrganizationIds } =
      getUserAndOrganizationIdsFromProject({
        type: 'form',
        data: values,
        projectId: id,
      });

    const isRemoveNotExistUser = notExistUserIds?.length
      ? Object.entries(getCountMapById(notExistUserIds)).some(
          ([userId, count]) =>
            inputUserIds.filter((v) => v === userId).length !== count,
        )
      : false;

    const isRemoveNotExistOrganization = notExistOrganizationIds?.length
      ? Object.entries(getCountMapById(notExistOrganizationIds)).some(
          ([organizationId, count]) =>
            inputOrganizationIds.filter((v) => v === organizationId).length !==
            count,
        )
      : false;

    if (isRemoveNotExistUser || isRemoveNotExistOrganization) {
      setOpenUpdateConfirmModal({
        isOpen: true,
        values,
      });
      return { isRemovedNotExistValue: true };
    }
    return { isRemovedNotExistValue: false };
  };

  return {
    checkRemoveNotExistUserIdOrNotExistOrganizationId,
    onCancelModal: () => {
      setOpenUpdateConfirmModal({ isOpen: false });
    },
    openUpdateConfirmModal,
  };
};

/**
 *
 * 事業年度（作成年度）が以下のいずれかに該当する場合はtrue
 * ①事業終了予定年度を過ぎた場合
 * ②短期アウトカムで設定した目標年度の翌年度
 * ③中期アウトカムで設定した目標年度の翌年度
 * ④長期アウトカムで設定した目標年度又は最終目標年度の翌年度
 */
export const useRequiredByEndYearOrOutcomeTargetYear = (projectId: string) => {
  const project = useProject(projectId);
  const fiscalYear = project.data?.fiscalYear ?? getCurrentFiscalYear();
  const indicatorsData = useIndicators({
    filters: { keyId: projectId },
  });

  const indicatorIds = indicatorsData.data?.map((v) => v.id);
  const { data: consecutiveIndicators } = useAllConsecutiveIndicators(
    indicatorIds ?? [],
  );

  const hasRequiredOutcomeTargetYear = useMemo(
    () =>
      !!indicatorsData.data?.some((v) => {
        const isOutcome = v.type === 'outcome';
        if (!isOutcome || v.dataNatureType === 'qualitative') {
          return false;
        }

        const isLongTerm = v.term === 'long';
        const consecutive = consecutiveIndicators?.[v.id];
        const nextYearsOfTargetYear =
          consecutive?.flatMap((v) =>
            v.isTargetYear ? [v.fiscalYear + 1] : [],
          ) ?? [];

        const lastYear = consecutive?.at(-1)?.fiscalYear;
        const nextYearOfLastYear =
          isLongTerm && lastYear ? lastYear + 1 : undefined;

        const hasRequiredYear = [
          ...nextYearsOfTargetYear,
          nextYearOfLastYear,
        ].some((v) => v === fiscalYear);
        return hasRequiredYear;
      }),
    [indicatorsData.data, consecutiveIndicators, fiscalYear],
  );

  return {
    required:
      (project.data?.endYear && project.data.endYear <= fiscalYear) ||
      hasRequiredOutcomeTargetYear,
    isLoading: project.isLoading || indicatorsData.isLoading,
  };
};
